import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { downloadFile, fetchBlob } from '../api/client'
import { Link, useSearchParams } from 'react-router-dom'
import {
  useApproveReceipt,
  useChangeReceiptOrderNumber,
  useChangeReceiptPaymentMethod,
  useRecordReceiptExport,
  useDeleteReceipt,
  useMailReceipts,
  useReceiptMeta,
  useReceipts,
  useRejectReceipt,
  useSubmitReceipt,
  type ReceiptView
} from '../api/queries'
import type { Receipt, ReceiptPaymentMethod } from '../api/types'
import { ReceiptScanner } from '../components/ReceiptScanner'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { useCreateRequest } from '../lib/createRequest'
import { formatDateTime } from '../lib/format'
import { useAuthedBlob } from '../lib/photos'

/** Ordrestyrings indbakke (beslutning 136) - serveren bestemmer adressen; den står her kun i spørgsmålet. */
const ORDER_MAIL = '21367@osworker.com'

const STATUS_COLORS: Record<string, string> = {
  pending: '#D97706',
  approved: '#16A34A',
  rejected: '#DC2626'
}

/**
 * Kvitteringer for mad på lange arbejdsdage (docs/DECISIONS.md beslutning 114).
 *
 * Montøren scanner kvitteringen og skriver ordrenummeret. Kontoret (Kontor og administratorer) godkender - så
 * ligger den i SharePoint under Kvitteringer/<år>/<måned> - eller afviser med en begrundelse.
 */
export function ReceiptsPage() {
  const { data: meta } = useReceiptMeta()
  const [adding, setAdding] = useState(false)
  const [params] = useSearchParams()
  const [view, setView] = useState<ReceiptView>(params.get('vis') === 'godkendelse' ? 'pending' : 'mine')
  // Soegning og maaned (beslutning 134). Soegningen venter et ojeblik, saa der ikke spoerges ved hvert tastetryk.
  const [searchText, setSearchText] = useState('')
  const [search, setSearch] = useState('')
  const [month, setMonth] = useState('')
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchText), 300)
    return () => window.clearTimeout(timer)
  }, [searchText])
  // Kontoret lander paa "Til godkendelse", naar der er nogen, der venter (Simon 2026-10-01) - men kun foerste gang
  // tallet kommer, saa et valgt faneblad ikke skifter under fingrene.
  const [chosen, setChosen] = useState(params.has('vis'))
  useEffect(() => {
    if (!chosen && meta) {
      if (meta.canDecide && meta.pendingCount > 0) setView('pending')
      setChosen(true)
    }
  }, [chosen, meta])

  useCreateRequest(['kvittering'], () => setAdding(true))

  const isOffice = meta?.canDecide ?? false

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <button className={`btn btn--sm ${view === 'mine' ? 'btn--primary' : ''}`} onClick={() => setView('mine')}>
            Mine
          </button>
          {isOffice && (
            <>
              <button
                className={`btn btn--sm ${view === 'pending' ? 'btn--primary' : ''}`}
                onClick={() => setView('pending')}
              >
                Til godkendelse
                {meta && meta.pendingCount > 0 && (
                  <span className="pill" style={{ background: '#DC2626', color: '#fff' }}>
                    {meta.pendingCount}
                  </span>
                )}
              </button>
              {/* Til bogholderiet (beslutning 134): godkendte, som ingen har hentet eller udskrevet endnu. */}
              <button
                className={`btn btn--sm ${view === 'notExported' ? 'btn--primary' : ''}`}
                onClick={() => setView('notExported')}
              >
                Ikke hentet
              </button>
              <button className={`btn btn--sm ${view === 'all' ? 'btn--primary' : ''}`} onClick={() => setView('all')}>
                Alle
              </button>
            </>
          )}
        </div>
        {!adding && (
          <button className="btn btn--primary" onClick={() => setAdding(true)}>
            Ny kvittering
          </button>
        )}
      </div>

      {adding && <SubmitForm onClose={() => setAdding(false)} />}

      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <input
          type="search"
          style={{ flex: '1 1 220px', maxWidth: 360 }}
          placeholder="Søg på ordrenummer eller navn"
          aria-label="Søg i kvitteringer"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
        />
        <input
          type="month"
          style={{ width: 170 }}
          aria-label="Måned"
          value={month}
          onChange={(event) => setMonth(event.target.value)}
        />
        {(searchText || month) && (
          <button
            className="btn btn--sm"
            onClick={() => {
              setSearchText('')
              setSearch('')
              setMonth('')
            }}
          >
            Nulstil
          </button>
        )}
      </div>

      <ReceiptList view={view} search={search} month={month} filtered={Boolean(search || month)} />
    </div>
  )
}

function SubmitForm({ onClose }: { onClose: () => void }) {
  const submit = useSubmitReceipt()
  const [orderNumber, setOrderNumber] = useState('')
  // Medarbejderudlæg eller firmakort (beslutning 148) - intet forvalg, så ingen sender det forkerte af vane.
  const [paymentMethod, setPaymentMethod] = useState<ReceiptPaymentMethod | null>(null)
  const [scan, setScan] = useState<Blob | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function send(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    if (!paymentMethod) {
      setError('Vælg om det er et medarbejderudlæg eller betalt med firmakort.')
      return
    }
    if (!scan) {
      setError('Tag et billede af kvitteringen først.')
      return
    }
    try {
      await submit.mutateAsync({ orderNumber: orderNumber.trim(), paymentMethod, file: scan })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kvitteringen kunne ikke sendes.')
    }
  }

  return (
    <Card title="Ny kvittering">
      <form className="stack" onSubmit={send}>
        <label className="field">
          <span className="field__label">Ordrenummer *</span>
          <input
            required
            inputMode="numeric"
            maxLength={20}
            placeholder="F.eks. 4850-1"
            style={{ maxWidth: 200 }}
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
          />
        </label>

        <div className="field">
          <span className="field__label">Hvordan er den betalt? *</span>
          <PaymentMethodChoice value={paymentMethod} onChange={setPaymentMethod} />
        </div>

        <div className="field">
          <span className="field__label">Kvitteringen *</span>
          <ReceiptScanner onScanned={setScan} />
        </div>

        {error && <div className="error-box">{error}</div>}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={submit.isPending || !scan || !paymentMethod}>
            {submit.isPending ? 'Sender ...' : 'Send til kontoret'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

/** De to betalingsmåder (beslutning 148) som store knapper - lette at ramme med en tommelfinger. */
const PAYMENT_METHODS: { value: ReceiptPaymentMethod; icon: string; label: string; hint: string }[] = [
  { value: 'employee', icon: '👷', label: 'Medarbejderudlæg', hint: 'Jeg har lagt ud og skal have pengene igen' },
  { value: 'company_card', icon: '💳', label: 'Betalt med firmakort', hint: 'Betalt med firmaets kort' }
]

function PaymentMethodChoice({
  value,
  onChange
}: {
  value: ReceiptPaymentMethod | null
  onChange: (value: ReceiptPaymentMethod) => void
}) {
  return (
    <div className="payment-choice" role="radiogroup" aria-label="Betalingsmåde">
      {PAYMENT_METHODS.map((m) => (
        <button
          key={m.value}
          type="button"
          role="radio"
          aria-checked={value === m.value}
          className={`payment-choice__option ${value === m.value ? 'payment-choice__option--on' : ''}`}
          onClick={() => onChange(m.value)}
        >
          <span aria-hidden style={{ fontSize: 24 }}>
            {m.icon}
          </span>
          <span>
            <strong>{m.label}</strong>
            <span className="tiny muted" style={{ display: 'block' }}>
              {m.hint}
            </span>
          </span>
        </button>
      ))}
    </div>
  )
}

function ReceiptList({
  view,
  search,
  month,
  filtered
}: {
  view: ReceiptView
  search: string
  month: string
  filtered: boolean
}) {
  const { data, isLoading, error } = useReceipts(view, search, month)
  const queryClient = useQueryClient()
  // Afkrydsede kvitteringer til ZIP (beslutning 134). Kun dem, der stadig staar paa listen, taeller.
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [zipping, setZipping] = useState(false)
  const [zipError, setZipError] = useState<string | null>(null)
  // Send videre til Ordrestyring (beslutning 136).
  const mail = useMailReceipts()
  const [mailNote, setMailNote] = useState<string | null>(null)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data || data.length === 0) {
    return (
      <EmptyState>
        {filtered
          ? 'Ingen kvitteringer passer til søgningen.'
          : view === 'pending'
            ? 'Ingen kvitteringer venter.'
            : view === 'notExported'
              ? 'Alle godkendte kvitteringer er hentet eller udskrevet.'
              : view === 'mine'
                ? 'Du har ikke sendt nogen kvitteringer. Tryk "Ny kvittering", når du har lagt ud for mad på en lang dag.'
                : 'Ingen kvitteringer endnu.'}
      </EmptyState>
    )
  }

  const chosen = data.filter((r) => selected.has(r.id)).map((r) => r.id)
  const allChosen = chosen.length === data.length

  function toggle(id: number) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function sendMail() {
    if (
      !window.confirm(
        `Send ${chosen.length === 1 ? 'kvitteringen' : `${chosen.length} kvitteringer`} til ${ORDER_MAIL}?\n\n` +
          'Der sendes én mail pr. kvittering med ordrenummeret i emnefeltet og kvitteringen vedhæftet.'
      )
    )
      return
    setZipError(null)
    setMailNote(null)
    try {
      const result = await mail.mutateAsync(chosen)
      const failed = result.failed.map((f) => `ordre ${f.orderNumber}: ${f.error}`).join('; ')
      setMailNote(
        `${result.sent} sendt til ${result.to}.` + (failed ? ` Ikke sendt - ${failed}` : '')
      )
      if (result.failed.length === 0) setSelected(new Set())
    } catch (err) {
      setZipError(err instanceof Error ? err.message : 'Kvitteringerne kunne ikke sendes.')
    }
  }

  async function downloadZip() {
    setZipError(null)
    setZipping(true)
    try {
      await downloadFile(`/api/receipts/zip?ids=${chosen.join(',')}`, 'Kvitteringer.zip')
      setSelected(new Set())
      // Hver er nu noteret som hentet.
      void queryClient.invalidateQueries({ queryKey: ['receipts'] })
    } catch (err) {
      setZipError(err instanceof Error ? err.message : 'ZIP-filen kunne ikke hentes.')
    } finally {
      setZipping(false)
    }
  }

  return (
    <div className="stack">
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <label className="row tiny" style={{ gap: 6, alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={allChosen}
            onChange={() => setSelected(allChosen ? new Set() : new Set(data.map((r) => r.id)))}
          />
          Vælg alle ({data.length})
        </label>
        {chosen.length > 0 && chosen.length <= 100 && (
          <button className="btn btn--sm btn--primary" disabled={zipping} onClick={() => void downloadZip()}>
            {zipping ? 'Pakker ...' : `⬇ Hent ${chosen.length} som ZIP`}
          </button>
        )}
        {chosen.length > 0 && chosen.length <= 50 && (
          <button className="btn btn--sm" disabled={mail.isPending} onClick={() => void sendMail()}>
            {mail.isPending ? 'Sender ...' : `✉ Send ${chosen.length} til Ordrestyring`}
          </button>
        )}
        {chosen.length > 100 && <span className="tiny warn">Højst 100 ad gangen - fravælg nogle.</span>}
        {chosen.length > 50 && chosen.length <= 100 && (
          <span className="tiny muted">Der kan sendes højst 50 på mail ad gangen.</span>
        )}
      </div>
      {zipError && <div className="small warn">{zipError}</div>}
      {mailNote && <div className="small" style={{ padding: 8, background: '#F0FDF4', borderRadius: 8 }}>✉ {mailNote}</div>}
      {data.map((receipt) => (
        <ReceiptCard
          key={receipt.id}
          receipt={receipt}
          showUser={view !== 'mine'}
          selected={selected.has(receipt.id)}
          onToggle={() => toggle(receipt.id)}
        />
      ))}
    </div>
  )
}

/**
 * Henter kvitteringen som PDF (beslutning 139) - filnavnet sætter serveren: "Kvittering 4850-1 Jens Hansen
 * 2026-10-01.pdf". Det, der forlader Overblik, er altid en PDF med brevhoved.
 */
function downloadReceiptPdf(receipt: Receipt) {
  return downloadFile(`/api/receipts/${receipt.id}/pdf`, `Kvittering ${receipt.orderNumber}.pdf`)
}

/**
 * Udskriver KUN kvitteringens PDF (beslutning 139) - i en skjult ramme, så resten af siden ikke kommer med, og uden
 * et nyt vindue, som appen på telefonen ellers ville blokere. PDF'en er A4, så den printer ens hver gang.
 */
async function printReceiptPdf(receipt: Receipt) {
  const blob = await fetchBlob(`/api/receipts/${receipt.id}/pdf`)
  if (!blob) throw new Error('Kvitteringen kunne ikke hentes til udskrift.')

  const url = URL.createObjectURL(blob)
  const frame = document.createElement('iframe')
  frame.style.position = 'fixed'
  frame.style.width = '0'
  frame.style.height = '0'
  frame.style.border = '0'
  frame.onload = () => {
    try {
      frame.contentWindow?.focus()
      frame.contentWindow?.print()
    } catch {
      // Kan browseren ikke printe fra rammen (nogle telefoner), åbnes PDF'en, så den kan printes derfra.
      window.open(url, '_blank')
    }
    // Rammen fjernes, når printdialogen er lukket - og ellers efter et minut.
    const cleanup = () => {
      frame.remove()
      URL.revokeObjectURL(url)
    }
    frame.contentWindow?.addEventListener('afterprint', cleanup)
    window.setTimeout(cleanup, 60_000)
  }
  frame.src = url
  document.body.appendChild(frame)
}

function ReceiptCard({
  receipt,
  showUser,
  selected,
  onToggle
}: {
  receipt: Receipt
  showUser: boolean
  selected: boolean
  onToggle: () => void
}) {
  const image = useAuthedBlob(receipt.imageUrl)
  const approve = useApproveReceipt()
  const reject = useRejectReceipt()
  const remove = useDeleteReceipt()
  const changeNumber = useChangeReceiptOrderNumber()
  const changePayment = useChangeReceiptPaymentMethod()
  const recordExport = useRecordReceiptExport()
  const [showExports, setShowExports] = useState(false)
  const downloads = receipt.exports.filter((x) => x.kind === 'download').length
  const prints = receipt.exports.filter((x) => x.kind === 'print').length
  const mailed = receipt.exports.filter((x) => x.kind === 'mail').length

  // Hver hentning og udskrift noteres (beslutning 133). Fejler noteringen, er filen hentet alligevel - det maa
  // ikke spaerre for montoeren eller kontoret.
  function noteExport(kind: 'download' | 'print') {
    recordExport.mutate({ id: receipt.id, kind })
  }
  const [editingNumber, setEditingNumber] = useState(false)
  const [newNumber, setNewNumber] = useState(receipt.orderNumber)
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function run(action: () => Promise<unknown>) {
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Det gik ikke.')
    }
  }

  return (
    <article className="card card--flat" style={{ padding: 14, borderLeft: `4px solid ${STATUS_COLORS[receipt.status] ?? '#64748B'}` }}>
      <div className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
        {/* Afkrydsning til "Hent som ZIP" (beslutning 134). */}
        <input
          type="checkbox"
          style={{ width: 'auto', marginTop: 4, flexShrink: 0 }}
          checked={selected}
          onChange={onToggle}
          aria-label={`Vælg kvitteringen på ordre ${receipt.orderNumber}`}
        />
        {image && receipt.isPdf ? (
          // Godkendt fra beslutning 139: arkivet er en PDF - den kan ikke vises som et billede, men åbnes.
          <a
            href={image}
            target="_blank"
            rel="noreferrer"
            title="Åbn PDF'en"
            style={{
              flexShrink: 0, width: 90, height: 120, borderRadius: 6, border: '1px solid var(--border)', background: '#F8FAFC',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
              textDecoration: 'none', color: 'var(--text-muted)'
            }}
          >
            <span style={{ fontSize: 30 }}>📄</span>
            <span className="tiny">PDF</span>
          </a>
        ) : image ? (
          <a href={image} target="_blank" rel="noreferrer" style={{ flexShrink: 0 }}>
            <img src={image} alt="Kvittering" style={{ width: 90, height: 120, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} />
          </a>
        ) : (
          <div style={{ width: 90, height: 120, borderRadius: 6, background: '#F1F5F9', flexShrink: 0 }} />
        )}

        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className="row" style={{ gap: 6, alignItems: 'baseline' }}>
              <strong>Ordre {receipt.orderNumber}</strong>
              {/* Ret ordrenummeret (beslutning 132). */}
              {receipt.canEditOrderNumber && !editingNumber && (
                <button
                  className="btn btn--sm"
                  style={{ padding: '2px 8px' }}
                  onClick={() => {
                    setNewNumber(receipt.orderNumber)
                    setEditingNumber(true)
                  }}
                >
                  ✏️ Ret
                </button>
              )}
            </span>
            <span className="pill" style={{ background: STATUS_COLORS[receipt.status], color: '#fff' }}>
              {receipt.statusName}
            </span>
          </div>
          {editingNumber && (
            <form
              className="row"
              style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}
              onSubmit={(event) => {
                event.preventDefault()
                void run(async () => {
                  await changeNumber.mutateAsync({ id: receipt.id, orderNumber: newNumber })
                  setEditingNumber(false)
                })
              }}
            >
              <input
                style={{ width: 140 }}
                inputMode="numeric"
                maxLength={20}
                autoFocus
                value={newNumber}
                onChange={(event) => setNewNumber(event.target.value)}
                aria-label="Ordrenummer"
                placeholder="F.eks. 4850-1"
              />
              <button className="btn btn--sm btn--primary" type="submit" disabled={changeNumber.isPending}>
                {changeNumber.isPending ? 'Gemmer ...' : 'Gem'}
              </button>
              <button className="btn btn--sm" type="button" onClick={() => setEditingNumber(false)}>
                Annuller
              </button>
              {receipt.status === 'rejected' && !receipt.canDecide && (
                <div className="tiny muted" style={{ width: '100%' }}>
                  Når du gemmer, sendes kvitteringen til godkendelse igen.
                </div>
              )}
              {receipt.status === 'approved' && (
                <div className="tiny muted" style={{ width: '100%' }}>
                  Filen i SharePoint får det nye nummer i navnet.
                </div>
              )}
            </form>
          )}
          {/* Betalingsmåden (beslutning 148). Kan skiftes indtil godkendelsen - en godkendt ligger i sin mappe. */}
          <div className="row" style={{ gap: 6, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <span
              className="pill"
              style={
                receipt.paymentMethod
                  ? { background: receipt.paymentMethod === 'company_card' ? '#E0E7FF' : '#FEF3C7', color: '#1E293B' }
                  : { background: '#DC2626', color: '#fff' }
              }
            >
              {receipt.paymentMethod === 'company_card' ? '💳 ' : receipt.paymentMethod === 'employee' ? '👷 ' : ''}
              {receipt.paymentMethodText}
            </span>
            {receipt.canEditOrderNumber && receipt.status !== 'approved' && (
              <select
                className="payment-select"
                aria-label="Skift betalingsmåde"
                value={receipt.paymentMethod ?? ''}
                disabled={changePayment.isPending}
                onChange={(event) =>
                  void run(() =>
                    changePayment.mutateAsync({ id: receipt.id, paymentMethod: event.target.value as ReceiptPaymentMethod })
                  )
                }
              >
                {!receipt.paymentMethod && <option value="">Vælg betalingsmåde ...</option>}
                <option value="employee">Medarbejderudlæg</option>
                <option value="company_card">Betalt med firmakort</option>
              </select>
            )}
          </div>
          <div className="tiny muted">
            {formatDateTime(receipt.createdAt)}
            {showUser && receipt.user && <> · {receipt.user.displayName}</>}
          </div>
          {receipt.projects.length > 0 && (
            <div className="tiny" style={{ marginTop: 4 }}>
              {receipt.projects.map((p, i) => (
                <span key={p.id}>
                  {i > 0 && ', '}
                  <Link to={`/projects/${p.id}`}>{p.name}</Link>
                </span>
              ))}
            </div>
          )}
          {receipt.status === 'rejected' && receipt.rejectReason && (
            <div className="small warn" style={{ marginTop: 6 }}>
              Afvist: {receipt.rejectReason}
            </div>
          )}
          {receipt.decidedBy && receipt.decidedAt && (
            <div className="tiny muted" style={{ marginTop: 4 }}>
              {receipt.statusName} af {receipt.decidedBy.displayName} {formatDateTime(receipt.decidedAt)}
            </div>
          )}
          {image && (
            <div className="btn-group" style={{ marginTop: 8 }}>
              <button
                className="btn btn--sm"
                onClick={() =>
                  void run(async () => {
                    await downloadReceiptPdf(receipt)
                    noteExport('download')
                  })
                }
              >
                ⬇ Hent
              </button>
              <button
                className="btn btn--sm"
                onClick={() =>
                  void run(async () => {
                    await printReceiptPdf(receipt)
                    noteExport('print')
                  })
                }
              >
                🖨 Udskriv
              </button>
            </div>
          )}
          {/* Hvor mange gange - og af hvem (beslutning 133). */}
          <div className="tiny" style={{ marginTop: 4 }}>
            {downloads + prints + mailed === 0 ? (
              <span className="muted">Ikke hentet eller udskrevet endnu</span>
            ) : (
              <button
                type="button"
                className="link-button"
                onClick={() => setShowExports((v) => !v)}
                aria-expanded={showExports}
              >
                <b>⬇ Hentet {downloads} {downloads === 1 ? 'gang' : 'gange'}</b> ·{' '}
                <b>🖨 Udskrevet {prints} {prints === 1 ? 'gang' : 'gange'}</b>
                {mailed > 0 && <> · <b>✉ Sendt {mailed} {mailed === 1 ? 'gang' : 'gange'}</b></>}{' '}
                <span className="muted">{showExports ? '▲' : '▼ hvem'}</span>
              </button>
            )}
          </div>
          {showExports && receipt.exports.length > 0 && (
            <ul className="tiny muted" style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {receipt.exports.map((x, i) => (
                <li key={i}>
                  {x.kind === 'print' ? '🖨 Udskrevet' : x.kind === 'mail' ? '✉ Sendt til Ordrestyring' : '⬇ Hentet'} af{' '}
                  {x.user?.displayName ?? 'ukendt'}{' '}
                  {formatDateTime(x.createdAt)}
                </li>
              ))}
            </ul>
          )}
          {receipt.webUrl && (
            <div className="tiny" style={{ marginTop: 4 }}>
              <a href={receipt.webUrl} target="_blank" rel="noreferrer">
                Åbn i SharePoint
              </a>
            </div>
          )}

          {(receipt.canDecide || receipt.canDelete) && !rejecting && (
            <div className="btn-group" style={{ marginTop: 10 }}>
              {receipt.canDecide && (
                <>
                  <button
                    className="btn btn--sm btn--primary"
                    disabled={approve.isPending}
                    onClick={() => void run(() => approve.mutateAsync(receipt.id))}
                  >
                    {approve.isPending ? 'Gemmer ...' : 'Godkend og gem'}
                  </button>
                  {/* En afvist kan godkendes bagefter, men ikke afvises igen (beslutning 132). */}
                  {receipt.status === 'pending' && (
                    <button className="btn btn--sm" onClick={() => setRejecting(true)}>
                      Afvis
                    </button>
                  )}
                </>
              )}
              {receipt.canDelete && (
                <button
                  className="btn btn--sm"
                  disabled={remove.isPending}
                  onClick={() => {
                    // En godkendt kan kun Superadmin slette - og saa forsvinder filen ogsaa fra SharePoint (beslutning 133).
                    const question =
                      receipt.status === 'approved'
                        ? 'Slet den godkendte kvittering? Filen slettes også i SharePoint (den lægges i papirkurven).'
                        : 'Slet kvitteringen?'
                    if (window.confirm(question)) void run(() => remove.mutateAsync(receipt.id))
                  }}
                >
                  Slet
                </button>
              )}
            </div>
          )}

          {rejecting && (
            <form
              className="stack"
              style={{ marginTop: 10 }}
              onSubmit={(event) => {
                event.preventDefault()
                void run(async () => {
                  await reject.mutateAsync({ id: receipt.id, reason })
                  setRejecting(false)
                })
              }}
            >
              <input
                autoFocus
                required
                maxLength={500}
                placeholder="Hvorfor? F.eks. Utydelig - tag billedet igen"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <div className="btn-group">
                <button className="btn btn--sm btn--primary" type="submit" disabled={reject.isPending}>
                  Afvis
                </button>
                <button className="btn btn--sm" type="button" onClick={() => setRejecting(false)}>
                  Fortryd
                </button>
              </div>
            </form>
          )}

          {error && <div className="error-box" style={{ marginTop: 8 }}>{error}</div>}
        </div>
      </div>
    </article>
  )
}
